/* eslint-disable react/require-default-props */
import React, {
  useRef, useState, useEffect, FocusEvent, HTMLProps, ReactElement,
} from 'react';
import {
  KatButton, KatIcon, KatInput, KatSpinner,
} from '@amzn/katal-react';

type HTMLInputElementProps = HTMLProps<HTMLInputElement>;

export type TypeaheadProps = {
  value: string,
  onChange: (e: KatInput.ChangeEvent) => void,
  onSearch: () => void,
  onFocusAction?: () => void,
  onBlurAction?: () => void,
  onSelect?: (value: any, index: number) => void,
  onPressEnter?: () => void,
  options?: Array<{ value: any, label: ReactElement, key?: string }>,
  isLoading?: boolean,
  delay: number,
  open?: boolean,
  searchButton?: boolean,
  clearButton?: boolean,
  noOption?: ReactElement | Array<ReactElement>,
  error?: string,
  minSearchLength?: number,
} & Omit<HTMLInputElementProps, 'onSelect'> & KatInput.Props;

const Typeahead: React.FC<TypeaheadProps> = ({
  value,
  onChange,
  onSelect = () => {},
  options = [],
  isLoading = false,
  onSearch,
  onPressEnter = () => {},
  delay,
  open = true,
  searchButton: showSearchButton = true,
  clearButton: showClearButton = true,
  noOption,
  error,
  minSearchLength = 3,
  onFocusAction = () => {},
  onBlurAction = () => {},
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFocus = () => {
    setIsFocused(true);
    onFocusAction();
  };
  // https://gist.github.com/pstoica/4323d3e6e37e8a23dd59
  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    const { currentTarget, relatedTarget } = e;
    onBlurAction();
    setTimeout(() => {
      // Check if the new activeElement is a child of the original container
      if (!currentTarget.contains(relatedTarget)) {
        setIsFocused(false);
      }
    }, 0);
  };

  let debounceTimer: ReturnType<typeof setTimeout>;

  useEffect(() => {
    if (delay > 0) {
      clearTimeout(debounceTimer);
      if (value!.trim().length >= minSearchLength) {
        setIsDebouncing(true);
        debounceTimer = setTimeout(() => {
          onSearch();
          setIsDebouncing(false);
        }, delay);
      } else {
        setIsDebouncing(false);
      }
    } else if (value!.trim().length >= minSearchLength) {
      onSearch();
    }

    return () => clearTimeout(debounceTimer);
  }, [value]);

  const showSpinner = isLoading || isDebouncing;

  let inputIcon = null;
  if (showClearButton && value!.length >= 1) {
    inputIcon = (
      <div className="absolute top-2 ltr:right-2 rtl:left-2">
        <button
          data-testid="clear-button"
          type="button"
          className="mb-2 cursor-pointer border-0 bg-transparent p-0"
          onClick={() => {
            inputRef.current!.value = '';
            inputRef.current!.dispatchEvent(new Event('input'));
            onBlurAction();
          }}
        >
          <KatIcon name="clear" size="small" />
        </button>
      </div>
    );
  }
  if (showSpinner && isFocused) {
    inputIcon = (
      <div
        data-testid="spinner"
        className="absolute top-[0.08rem] ltr:right-[0.18rem] rtl:left-[0.18rem]"
      >
        <KatSpinner size="small" />
      </div>
    );
  }

  let optionsRender = noOption;
  if (options!.length > 0) {
    optionsRender = options!.map(({ value: optionValue, label, key }, index) => {
      const handleSelect = () => {
        onSelect(optionValue, index);
        setIsFocused(false);
      };
      return (
        <div
          data-testid="option"
          role="option"
          aria-selected={false}
          key={key || optionValue}
        >
          <div
            role="button"
            className="cursor-pointer"
            onClick={handleSelect}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSelect();
            }}
            tabIndex={0}
          >
            {label}
          </div>
        </div>
      );
    });
  }

  const showOptions = open && isFocused && value!.trim().length >= minSearchLength
                      && !showSpinner && optionsRender;

  return (
    <div
      data-testid="typeahead"
      className="relative"
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="flex">
        <div className="relative w-full">
          <KatInput
            {...rest}
            className={`typeahead-input w-full bg-kat-white ltr:border-r-0 rtl:border-l-0 ${showSearchButton ? 'ltr:typeahead-input-with-search-button-ltr rtl:typeahead-input-with-search-button-rtl ltr:rounded-r-none rtl:rounded-l-none' : ''}`}
            autoComplete="off"
            // @ts-ignore
            ref={inputRef}
            value={value}
            onInput={onChange}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                onPressEnter();
              }
            }}
            state={error ? 'error' : undefined}
            stateLabel={error}
          />
          {inputIcon}
        </div>
        {showSearchButton && (
          <KatButton
            data-testid="search-button"
            className="typeahead-search-button ltr:rounded-l-none rtl:rounded-r-none"
            onClick={onPressEnter}
          >
            <KatIcon name="search" size="small" />
          </KatButton>
        )}
      </div>
      {showOptions && (
        <div
          data-testid="options-menu"
          className="absolute z-10 max-h-80 w-full overflow-auto border border-solid border-kat-casper bg-kat-white drop-shadow-lg"
          role="listbox"
        >
          {optionsRender}
        </div>
      )}
    </div>
  );
};

export default Typeahead;
